/**
 * Cart Shipping Threshold Calculator
 * Converts free shipping threshold for different currencies
 * Updates live with smooth animations in cart drawer
 */
class ShippingThresholdCalculator {
  constructor() {
    this.baseThresholdGBP = 40;
    this.currencyRates = {
      'GBP': 1,
      'USD': 1.30,
      'AUD': 2.14,
      'EUR': 1.16,
      'CAD': 1.78,
      'ZAR': 20.0
    };
    this.currencySymbols = {
      'GBP': '£', 'USD': '$', 'AUD': 'A$',
      'EUR': '€', 'CAD': 'C$', 'ZAR': 'R'
    };
    this.previousCartTotal = 0;
    this.previousPercent = 0;
    this._cachedElements = null;
    this.init();
  }

  init() {
    this.currentCurrency = (Shopify && Shopify.currency) ? Shopify.currency.active : 'GBP';
    this.currencyThreshold = this.currencyRates[this.currentCurrency]
      ? Math.round(this.baseThresholdGBP * this.currencyRates[this.currentCurrency])
      : this.baseThresholdGBP;
    this.setupEventListeners();
    setTimeout(() => this.updateUI(), 50);
  }

  get elements() {
    if (!this._cachedElements || !document.body.contains(this._cachedElements.progressBar)) {
      this._cachedElements = {
        checkoutButton: document.querySelector('#CartDrawer-Checkout .totals__total-value'),
        totalsElement: document.querySelector('.cart-drawer__footer .totals__total-value'),
        progressBar: document.querySelector('.progress_shipping .progress-meter'),
        progressContainer: document.querySelector('.progress'),
        progressShipping: document.querySelector('.progress_shipping'),
        progressAmount: document.querySelector('.progress_amount'),
        progressRemain: document.querySelector('.progress_remain'),
        freeShipping: document.querySelector('.free_shipping'),
        shippingText: document.querySelector('.shipping'),
        currencySymbol: document.querySelector('.currency_symbol'),
        cartDrawer: document.querySelector('cart-drawer')
      };
    }
    return this._cachedElements;
  }

  invalidateCache() {
    this._cachedElements = null;
  }

  setupEventListeners() {
    if (typeof subscribe !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe('cart-update', () => {
        this.invalidateCache();
        setTimeout(() => this.updateUI(), 100);
      });
    }
    this.setupCartDrawerListener();
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.updateUI());
    }
  }

  setupCartDrawerListener() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      const originalOpen = cartDrawer.open;
      cartDrawer.open = (...args) => {
        originalOpen.apply(cartDrawer, args);
        document.dispatchEvent(new CustomEvent('cart-drawer:open'));
        this.invalidateCache();
        setTimeout(() => this.updateUI(), 100);
      };
    }
  }

  getCartTotal() {
    const el = this.elements;
    const sources = [el.checkoutButton, el.totalsElement];

    for (const source of sources) {
      if (source) {
        const dataAttr = source.getAttribute('data');
        if (dataAttr) {
          const val = parseFloat(dataAttr.replace(',', '.'));
          if (!isNaN(val)) return val;
        }
      }
    }

    const allTotals = document.querySelectorAll('.totals__total-value[data]');
    for (const element of allTotals) {
      const dataAttr = element.getAttribute('data');
      if (dataAttr) {
        const val = parseFloat(dataAttr.replace(',', '.'));
        if (!isNaN(val)) return val;
      }
    }

    const textElements = document.querySelectorAll('.totals__total-value');
    for (const element of textElements) {
      const match = element.textContent.trim().match(/[\d,]+\.?\d*/);
      if (match) {
        const val = parseFloat(match[0].replace(',', '.'));
        if (!isNaN(val)) return val;
      }
    }

    return 0;
  }

  hasCartItems() {
    const cartItems = document.querySelectorAll('.cart-item');
    if (cartItems.length > 0) return true;
    const cartDrawer = this.elements.cartDrawer;
    return cartDrawer ? !cartDrawer.classList.contains('is-empty') : false;
  }

  updateUI() {
    const cartTotal = this.getCartTotal();

    if (cartTotal === this.previousCartTotal && this.previousCartTotal !== 0) return;
    this.previousCartTotal = cartTotal;

    const remaining = Math.max(0, this.currencyThreshold - cartTotal);
    let percent = Math.min(100, Math.max(0, (cartTotal / this.currencyThreshold) * 100));

    if (this.hasCartItems() && cartTotal === 0) percent = 100;

    this.updateProgressBar(percent);
    this.updateProgressClasses(percent);
    this.updateCompleteState(percent);
    this.updateTextElements(remaining, percent);
    this.updateCurrencySymbol();
    this.previousPercent = percent;
  }

  updateProgressBar(percent) {
    const bar = this.elements.progressBar;
    if (!bar) return;
    const current = parseFloat(bar.style.width) || 0;
    if (Math.abs(current - percent) > 1 || this.previousCartTotal === 0) {
      bar.style.transition = 'width 0.6s ease-out';
      bar.style.width = `${percent}%`;
      setTimeout(() => { bar.style.transition = ''; }, 600);
    } else {
      bar.style.transition = 'none';
      bar.style.width = `${percent}%`;
      requestAnimationFrame(() => { bar.style.transition = 'width 0.6s ease-out'; });
    }
  }

  updateProgressClasses(percent) {
    const container = this.elements.progressContainer;
    if (!container) return;
    container.classList.remove('progress-0', 'progress-25', 'progress-50', 'progress-75', 'progress-100');
    const level = percent >= 100 ? 100 : percent >= 75 ? 75 : percent >= 50 ? 50 : percent >= 25 ? 25 : 0;
    container.classList.add(`progress-${level}`);
  }

  updateCompleteState(percent) {
    const el = this.elements.progressShipping;
    if (!el) return;
    el.classList.toggle('complete', percent >= 100);
  }

  updateTextElements(remaining, percent) {
    const el = this.elements;
    if (el.progressAmount) {
      el.progressAmount.textContent = (this.previousCartTotal === 0)
        ? this.currencyThreshold.toFixed(2)
        : remaining.toFixed(2);
    }

    const finalHasItems = this.hasCartItems();

    if (el.progressRemain && el.freeShipping) {
      const showFree = (percent >= 100) || (finalHasItems && this.previousCartTotal === 0);
      if (showFree) {
        el.progressRemain.style.opacity = '0';
        setTimeout(() => {
          el.progressRemain.style.display = 'none';
          el.freeShipping.style.display = 'block';
          requestAnimationFrame(() => { el.freeShipping.style.opacity = '1'; });
        }, 150);
      } else {
        el.freeShipping.style.opacity = '0';
        setTimeout(() => {
          el.freeShipping.style.display = 'none';
          el.progressRemain.style.display = 'block';
          requestAnimationFrame(() => { el.progressRemain.style.opacity = '1'; });
        }, 150);
      }
    }

    if (el.shippingText) {
      el.shippingText.textContent = 'FREE Shipping';
      el.shippingText.style.color = percent >= 100 ? '#00a651' : '';
    }
  }

  updateCurrencySymbol() {
    const el = this.elements.currencySymbol;
    if (el) el.textContent = this.currencySymbols[this.currentCurrency] || '£';
  }

  disconnect() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }
}

// Initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.shippingThresholdCalculator = new ShippingThresholdCalculator();
  });
} else {
  window.shippingThresholdCalculator = new ShippingThresholdCalculator();
}

document.addEventListener('cart-drawer:open', () => {
  if (window.shippingThresholdCalculator) {
    window.shippingThresholdCalculator.invalidateCache();
    setTimeout(() => window.shippingThresholdCalculator.updateUI(), 100);
  }
});

// Hook into CartDrawer.renderContents for live updates
(function hookCartDrawer() {
  if (typeof CartDrawer !== 'undefined') {
    const orig = CartDrawer.prototype.renderContents;
    CartDrawer.prototype.renderContents = function(...args) {
      orig.apply(this, args);
      if (window.shippingThresholdCalculator) {
        window.shippingThresholdCalculator.invalidateCache();
        setTimeout(() => window.shippingThresholdCalculator.updateUI(), 100);
      }
    };
  } else {
    const check = setInterval(() => {
      if (typeof CartDrawer !== 'undefined') {
        clearInterval(check);
        hookCartDrawer();
      }
    }, 200);
  }
})();

// Hook into CartItems.updateQuantity
if (typeof CartItems !== 'undefined') {
  const origUpdate = CartItems.prototype.updateQuantity;
  CartItems.prototype.updateQuantity = function(...args) {
    const result = origUpdate.apply(this, args);
    if (window.shippingThresholdCalculator) {
      window.shippingThresholdCalculator.invalidateCache();
      setTimeout(() => window.shippingThresholdCalculator.updateUI(), 200);
    }
    return result;
  };
}
